import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  // 2026-10-01: a failed or incomplete section response (rate limit, network) no longer throws an
  // uncaught error. Retry once after 2s; if that also fails, keep the server-rendered markup as is.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });
      section.dataset.hydrated = 'true';
      return;
    } catch (error) {
      if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}
